import { Alert, AlertIcon, AlertTitle, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Button } from '@chakra-ui/react';
import React from 'react';
import { InvestPreflightHint } from '@services/appsync/types';
import PlaidLink from 'src/apps/invest/common/components/PlaidLink';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/apps/invest/config';

type PreflightModalProps = {
    preflightHint: InvestPreflightHint;
    onClose: () => void;
};

export const PreflightModal: React.FC<PreflightModalProps> = ({ preflightHint, onClose }) => {
    const [linkError, setLinkError] = React.useState<string | null>(null);
    const bankAccountsModel = useBankAccountsModel();
    const navigate = useNavigate();

    const alertText = (preflightHint === InvestPreflightHint.BANK_ACCOUNT_ITEM_LOGIN_REQUIRED)
        ? 'Your bank account must be re-linked before investing. Please continue to Plaid, our banking partner, to finish the process.'
        : (preflightHint === InvestPreflightHint.LEGACY_BANK_ACCOUNT)
            ? 'Your bank account link is outdated and must be re-linked before investing. Please continue to Plaid, our banking partner, to finish the process.'
            : (preflightHint === InvestPreflightHint.NO_DEFAULT_BANK_ACCOUNT)
                ? 'You have no bank account set. Please continue to Plaid, our banking partner, to link your bank account.'
                : (preflightHint === InvestPreflightHint.ACCEPT_DWOLLA_TC)
                    ? 'You need to accept the Dwolla Terms of Service before proceeding. Please click the button below to review and accept the terms.'
                    : ''
    const showModal = preflightHint in InvestPreflightHint;  // Only show modal if the preflight hint is a valid value
    const defaultBankAccount = bankAccountsModel.getDefaultBankAccount();  // Would be better as a reactive variable

    const onStart = () => {
        setLinkError(null);
    }

    const finishBankReLink = async (publicToken: string, metadata: any) => {
        const existingBankAccountId = preflightHint === InvestPreflightHint.BANK_ACCOUNT_ITEM_LOGIN_REQUIRED ? defaultBankAccount?.id : null;
        try {
            const res = await bankAccountsModel.addBankAccount(publicToken, metadata, existingBankAccountId);
            if (res.success) {
                onClose();
            } else if (res.error) {
                setLinkError(res.error);
            }
        } catch (err: any) {
            setLinkError('Unable to add bank account');
        }
    }

    const handleDwollaTerms = () => {
        navigate(ROUTES.inboxDwollaAlert);
        onClose();
    };

    return (
        <Modal isOpen={showModal} closeOnEsc={false} onClose={onClose} size={['full', 'full', 'full', 'lg']}>
            <ModalOverlay backgroundColor="blackAlpha.100" backdropFilter="blur(5px)" />
            <ModalContent>
                <ModalHeader textAlign={'center'}>{preflightHint === InvestPreflightHint.ACCEPT_DWOLLA_TC ? 'Action Required' : 'Link Bank Account'}</ModalHeader>

                <ModalBody display="flex" flexDirection={'column'} gap="20px">
                    <Alert status={linkError ? 'error' : 'info'}>
                        <AlertIcon />
                        <AlertTitle>{linkError || alertText}</AlertTitle>
                    </Alert>
                </ModalBody>
                <ModalFooter justifyContent={'center'} padding="30px">
                    {preflightHint === InvestPreflightHint.ACCEPT_DWOLLA_TC ? (
                        <Button onClick={handleDwollaTerms} colorScheme="blue">
                            Review Dwolla Terms
                        </Button>
                    ) : (
                        <PlaidLink
                            onStart={onStart}
                            onSuccess={finishBankReLink}
                            buttonProps={{
                                children: linkError ? 'Try Again' : 'Continue',
                                colorScheme: "blue",
                            }}
                            currentBankAccountId={preflightHint === InvestPreflightHint.BANK_ACCOUNT_ITEM_LOGIN_REQUIRED ? defaultBankAccount?.id : null}
                        />
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PreflightModal;
